import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import API_ROUTES from './apiRoutes';

export const useFetch = () => {
    const navigate = useNavigate();

    const handleUnauthorized = useCallback(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        navigate('/login');
    }, [navigate]);

    const refreshAccessToken = useCallback(async (): Promise<string | null> => {
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
            return null;
        }

        try {
            const refreshUrl = API_ROUTES.AUTH.refresh(refreshToken);
            
            const response = await fetch(refreshUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                return null;
            }

            const result = await response.json();
            const newAccessToken = result.access_token;
            
            if (!newAccessToken) {
                return null;
            }
            
            localStorage.setItem('accessToken', newAccessToken);
            return newAccessToken;
        } catch (error) {
            return null;
        }
    }, []);

    const fetchWithAuth = useCallback(async (url: string, options: RequestInit = {}) => {
        const token = localStorage.getItem('accessToken');

        if (!token) {
            handleUnauthorized();
            throw new Error('No access token available');
        }

        try {
            const response = await fetch(url, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    ...options.headers,
                },
            });

            // Se receber 401 ou 403, tenta refresh do token
            if (response.status === 401 || response.status === 403 || response.status === 500) {
                const newAccessToken = await refreshAccessToken();
                
                if (newAccessToken) {
                    // Reenvia a requisição com o novo token
                    const retryResponse = await fetch(url, {
                        ...options,
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${newAccessToken}`,
                            ...options.headers,
                        },
                    });
                    
                    // Se ainda der erro após refresh, fazer logout
                    if (retryResponse.status === 401 || retryResponse.status === 403) {
                        handleUnauthorized();
                        throw new Error('Unauthorized after token refresh');
                    }
                    
                    return retryResponse;
                } else {
                    handleUnauthorized();
                    throw new Error('Failed to refresh token');
                }
            }

            return response;
        } catch (error) {
            // Se for erro de rede, propaga
            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw new Error('Network error');
            }
            throw error;
        }
    }, [refreshAccessToken, handleUnauthorized]);

    return { fetchWithAuth };
};
